"use client";

import { CheckIcon, UploadIcon } from "lucide-react";
import type * as React from "react";

import { cn } from "../cn";
import { FOCUS_RING } from "./focus-ring";
import { CARD_HOVER } from "./states";

// Design spec §6.9: the steps strip (setup checklist, import wizard), the wizard frame and the
// dashed upload zone.

export type StepState = "todo" | "current" | "done";

const NUMBER_STATE: Record<StepState, string> = {
  todo: "border-border text-muted-foreground",
  current: "border-primary-soft bg-primary-soft text-primary-text",
  done: "border-success-soft bg-success-soft text-success-text",
};

export type StepNumberProps = Omit<React.ComponentProps<"span">, "children"> & {
  n: number;
  state?: StepState;
};

/** 28 px circle: hairline and muted when to do, accent when current, a check when done. */
export function StepNumber({ n, state = "todo", className, ...props }: StepNumberProps) {
  return (
    <span
      data-slot="step-number"
      data-state={state}
      aria-hidden="true"
      className={cn(
        "inline-flex size-7 shrink-0 items-center justify-center rounded-full border text-xs font-medium tabular-nums",
        NUMBER_STATE[state],
        className,
      )}
      {...props}
    >
      {state === "done" ? <CheckIcon className="size-3.5" /> : n}
    </span>
  );
}

export type SetupStep = {
  label: React.ReactNode;
  hint?: React.ReactNode;
  state: StepState;
  href?: string;
  onSelect?: () => void;
};

export type SetupStepsProps = Omit<React.ComponentProps<"ol">, "children"> & {
  steps: SetupStep[];
};

const STATE_TEXT: Record<StepState, string> = { todo: "", current: "", done: " (done)" };

/**
 * Equal columns 12 px apart (one column below 1200); each step a radius-8 hairline tile, its
 * border darkening on hover when it links somewhere. The current step is `aria-current="step"`.
 */
export function SetupSteps({ steps, className, ...props }: SetupStepsProps) {
  return (
    <ol
      data-slot="setup-steps"
      className={cn("grid gap-3 min-[1200px]:auto-cols-fr min-[1200px]:grid-flow-col", className)}
      {...props}
    >
      {steps.map((step, i) => {
        const body = (
          <>
            <StepNumber n={i + 1} state={step.state} />
            <span className="min-w-0">
              <strong className="block text-xs font-semibold text-foreground">
                {step.label}
                <span className="sr-only">{STATE_TEXT[step.state]}</span>
              </strong>
              {step.hint ? (
                <span className="mt-1 block text-2xs text-muted-foreground">{step.hint}</span>
              ) : null}
            </span>
          </>
        );
        const tile = cn(
          "flex h-full w-full items-center gap-3 rounded-md border border-border bg-card px-3.5 py-[18px] text-left",
          step.state === "current" && "border-primary-soft",
        );
        const current = step.state === "current" ? ("step" as const) : undefined;
        return (
          // biome-ignore lint/suspicious/noArrayIndexKey: steps are positional and never reorder
          <li key={i} data-slot="setup-step" data-state={step.state} className="min-w-0">
            {step.href ? (
              <a
                href={step.href}
                aria-current={current}
                className={cn(tile, CARD_HOVER, FOCUS_RING)}
              >
                {body}
              </a>
            ) : step.onSelect ? (
              <button
                type="button"
                onClick={step.onSelect}
                aria-current={current}
                className={cn(tile, CARD_HOVER, FOCUS_RING)}
              >
                {body}
              </button>
            ) : (
              <div aria-current={current} className={tile}>
                {body}
              </div>
            )}
          </li>
        );
      })}
    </ol>
  );
}

export type WizardProps = Omit<React.ComponentProps<"div">, "children"> & {
  steps: { label: React.ReactNode; hint?: React.ReactNode }[];
  /** Zero-based index of the step in progress; earlier steps read as done. */
  current: number;
  /** The current step's section card(s). */
  children: React.ReactNode;
  /** Back (secondary) · Continue (primary lg), right-aligned 24 px below. */
  footer?: React.ReactNode;
  "aria-label"?: string;
};

export function Wizard({
  steps,
  current,
  children,
  footer,
  className,
  "aria-label": ariaLabel = "Steps",
  ...props
}: WizardProps) {
  return (
    <div data-slot="wizard" className={cn("flex flex-col", className)} {...props}>
      <SetupSteps
        aria-label={ariaLabel}
        className="mb-6"
        steps={steps.map((s, i) => ({
          ...s,
          state: i < current ? "done" : i === current ? "current" : "todo",
        }))}
      />
      {children}
      {footer ? (
        <div data-slot="wizard-footer" className="mt-6 flex justify-end gap-2">
          {footer}
        </div>
      ) : null}
    </div>
  );
}

type IconType = React.ComponentType<{ className?: string; "aria-hidden"?: boolean }>;

export type UploadZoneProps = Omit<React.ComponentProps<"input">, "type" | "title"> & {
  title: React.ReactNode;
  hint?: React.ReactNode;
  icon?: IconType;
  onFiles?: (files: File[]) => void;
};

/** 1.5 px dashed --border-strong, radius 10, padding 32 20, --sunken; ring on focus within. */
export function UploadZone({
  title,
  hint,
  icon: Icon = UploadIcon,
  onFiles,
  onChange,
  className,
  ...props
}: UploadZoneProps) {
  return (
    <label
      data-slot="upload-zone"
      className={cn(
        "flex cursor-pointer flex-col items-center gap-2 rounded-[10px] border-[1.5px] border-dashed border-strong bg-sunken px-5 py-8 text-center hover:border-primary focus-within:outline-2 focus-within:outline-offset-2 focus-within:outline-ring focus-within:outline-solid has-disabled:cursor-not-allowed has-disabled:opacity-55",
        "transition-colors duration-(--dur-fast)",
        className,
      )}
    >
      <Icon className="size-6 text-primary" aria-hidden={true} />
      <span className="text-sm font-ui text-foreground">{title}</span>
      {hint ? <span className="text-xs text-muted-foreground">{hint}</span> : null}
      <input
        type="file"
        className="sr-only"
        onChange={(e) => {
          onChange?.(e);
          onFiles?.(Array.from(e.currentTarget.files ?? []));
        }}
        {...props}
      />
    </label>
  );
}
